import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const ORDER_NUMBER_PATTERN = /^DD-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const ORDER_NUMBER_SOURCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

export const OrderNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^DD-[0-9]{8}-[0-9A-F]{12}$",
});
export type OrderNumber = Static<typeof OrderNumberSchema>;

function isRealBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function hasSufficientSourceEntropy(value: string): boolean {
  return (
    ORDER_NUMBER_SOURCE_PATTERN.test(value) &&
    [...value].filter((character) => /[A-Za-z0-9]/u.test(character)).length >= 12
  );
}

function deriveFortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function isOrderNumber(value: unknown): value is OrderNumber {
  if (!Value.Check(OrderNumberSchema, value)) return false;
  const match = ORDER_NUMBER_PATTERN.exec(value);
  if (!match) return false;
  return isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`);
}

export function deriveOrderNumber(businessDate: string, uuidOrOrderId: string): OrderNumber {
  if (!isRealBusinessDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!hasSufficientSourceEntropy(uuidOrOrderId)) {
    throw new TypeError("uuidOrOrderId must contain at least 12 safe alphanumeric characters");
  }
  const compactDate = businessDate.replaceAll("-", "");
  return `DD-${compactDate}-${deriveFortyEightBitHex(`${businessDate}\0${uuidOrOrderId}`)}`;
}

export function isOrderNumberForOrderId(
  value: unknown,
  orderId: string,
  businessDate: string,
): value is OrderNumber | string | undefined {
  if (value === undefined) return true;
  if (typeof value !== "string") return false;
  if (value === orderId) return true;
  if (!isOrderNumber(value)) return false;
  try {
    return value === deriveOrderNumber(businessDate, orderId);
  } catch {
    return false;
  }
}
